/**
 * Game Composable
 * 管理遊戲列表、分頁載入、進入遊戲、演示模式
 * 原始來源: cuora-module/src/machine/game_v2_1.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/game/game.js)
 * - 業務邏輯：保留在 composable 中
 */

import { storeToRefs } from 'pinia'
import { nextTick, computed, onBeforeUnmount, getCurrentInstance } from 'vue'

import { gameApi } from '@/api'
import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useSite } from '@/composables/core/useSite'
import { useGameRouter } from '@/composables/game/useGameRouter'
import { useUserInfo } from '@/composables/user/useUserInfo'
import { useGameStore } from '@/stores/game/game'
import { useMainStore } from '@/stores/main'
import { isTrue } from '@/utils/siteConfigChecker'

const GAME_SEARCH = 'GameSearch'

export const useGame = () => {
  const nuxtApp = useNuxtApp()
  const { $request } = nuxtApp
  const { appSecret, clientId } = useRuntimeConfig().public
  const { getUserInfo } = useUserInfo()
  const { token } = useAuthToken()
  const { selectWalletEnable, checkActivityWalletRule, gameInformationSummaryEnable, checkBalance } = useSite()
  const gameRouter = useGameRouter()
  const store = useGameStore()
  const mainStore = useMainStore()

  const withdrawRule = computed(() => getUserInfo.value?.withdraw_rule)
  const uidSite = computed(() => getUserInfo.value?.uid_site)

  // 統一錯誤處理函數
  const handleGameError = (error, context, fallbackStatus = 'idle') => {
    setGameURL({ gameURL: null })
    store.setGameStatus(fallbackStatus)

    return error
  }

  // 狀態轉換工具函數
  const transitionGameStatus = (fromStatus, toStatus) => {
    const { gameStatus } = storeToRefs(store)
    if (gameStatus.value === fromStatus || !fromStatus) {
      store.setGameStatus(toStatus)
      return true
    }
    return false
  }

  // 清理遊戲狀態的工具函數
  const resetGameState = (clearGameInfo = true) => {
    setGameURL({ gameURL: null })
    if (clearGameInfo) {
      store.clearGameInfo()
    }
    store.setGameStatus('idle')
  }

  const {
    base,
    isLastPage,
    isLoadingMore,
    room,
    index,
    content,
    addition,
    game,
    walletType,
    lang,
    entry,
    isMobile,
    gameURL,
    gameToken,
    gameHtml,
    gameType,
    gameWalletType,
    isPlaying,
    gameStatus,
    hasNext,
    hasRoomAndIsNotInGame,
  } = storeToRefs(store)

  // SSR-safe: 使用 nuxtApp context 存儲 timer，避免記憶體洩漏
  if (!nuxtApp._gameContext) {
    nuxtApp._gameContext = {
      updateTimer: null,
      // SSR cleanup 方法
      cleanup: () => {
        const ctx = nuxtApp._gameContext
        if (ctx?.updateTimer) {
          clearInterval(ctx.updateTimer)
          ctx.updateTimer = null
        }
      },
    }
  }
  const ctx = nuxtApp._gameContext

  const isDifferentRoom = (newRoom) => {
    return room.value?.categoryId !== newRoom?.categoryId
  }

  const additionEnable = computed(() => isTrue(gameInformationSummaryEnable.value))

  const setLastPage = () => {
    store.setIsLastPage(index.value >= content.value?.totalPage - 1)
  }

  const setRoom = (roomValue) => {
    if (roomValue && isDifferentRoom(roomValue)) {
      store.setRoom(roomValue)
      resetContent()
      load()
    }
  }

  const recoverSetRoom = (roomValue) => {
    if (roomValue) {
      store.setRoom(roomValue)
      resetContent()
      load()
    }
  }

  const resetContent = () => {
    store.resetContent()
  }

  const increaseIndex = () => {
    store.increaseIndex()
  }

  const buildUniqueData = (existingData, newData) => {
    const existingKeys = new Set(existingData.map((item) => item.gameCode || item.categoryId).filter(Boolean))
    return existingData.concat(
      newData.filter((item) => {
        const key = item.gameCode || item.categoryId
        return !key || !existingKeys.has(key)
      })
    )
  }

  const appendContent = (data) => {
    if (room.value?.categoryId === GAME_SEARCH) {
      return
    } else if (additionEnable.value) {
      const [contentData, additionData = {}] = data
      store.setContent({
        ...content.value,
        ...contentData,
        data: buildUniqueData(content.value?.data || [], contentData?.data || []),
      })
      store.setAddition({
        ...addition.value,
        ...additionData,
        data: buildUniqueData(addition.value?.data || [], additionData?.data || []),
      })
    } else {
      const [contentData] = data
      store.setContent({
        ...content.value,
        ...contentData,
        data: buildUniqueData(content.value?.data || [], contentData?.data || []),
      })
      // 當 additionEnable 為 false 時，確保 addition 有正確的結構
      // 避免 addition.value 變成 undefined，防止後續讀取時報錯
      if (!addition.value || !addition.value.data) {
        store.setAddition({
          data: [],
          timestamp: null,
          totalPage: 0,
        })
      }
    }
  }

  const swapContent = (data) => {
    const [contentData, additionData] = data
    if (contentData?.data?.length) {
      store.setContent(contentData)
    }
    if (additionData?.data?.length) {
      store.setAddition(additionData)
    }
  }

  const clearGameInfo = () => {
    store.clearGameInfo()
  }

  const setGameInfo = (gameData) => {
    const { providerId, gameId, gameTypeId, mappingId, portrait, gameCode, name, gameName } = gameData.game
    store.setGame({
      providerId,
      gameId,
      gameTypeId,
      mappingId,
      portrait: portrait ? 1 : 0,
      gameCode,
      name,
      gameName,
    })
  }

  const setGameURL = (data) => {
    store.setGameURL(data.gameURL)
    store.setGameToken(data.gameToken)
    store.setGameHtml(data.gameHtml)
    store.setGameType(data.type)
    store.setGameWalletType(data.gameWalletType)
  }

  const setWalletType = (walletTypeValue) => {
    store.setWalletType(walletTypeValue)
  }

  const setLang = (locale) => {
    store.setLang(locale.replace('-', '_'))
  }

  const setEntry = (entryValue) => {
    store.setEntry(entryValue)
  }

  const setIsPlaying = (isPlayingValue) => {
    store.setIsPlaying(isPlayingValue)
  }

  const setIsLoadingMore = (loadingValue) => {
    store.setIsLoadingMore(loadingValue)
  }

  const getPageContent = async () => {
    if (room.value?.categoryId === GAME_SEARCH) {
      return
    } else if (!token.value) {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/getPageContent] categoryId is required but not provided')
        return []
      }
      const param = { categoryId: room.value.categoryId, timestamp: null, index: index.value, appSecret }
      const data = await $request.send(gameApi.getListCategory(param))
      return [data]
    } else if (additionEnable.value) {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/getPageContent] categoryId is required but not provided')
        return []
      }
      const param = { categoryId: room.value.categoryId, timestamp: null, index: index.value, appSecret }
      const [categoryRes, additionRes] = await Promise.all([
        $request.send(gameApi.getListCategory(param)),
        $request.send(gameApi.getListAddition(param)),
      ])
      return [categoryRes, additionRes]
    } else {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/getPageContent] categoryId is required but not provided')
        return []
      }
      const param = { categoryId: room.value.categoryId, timestamp: null, index: index.value, appSecret }
      const data = await $request.send(gameApi.getListCategory(param))
      return [data]
    }
  }

  const updatePageContent = async () => {
    if (room.value.categoryId === GAME_SEARCH) {
      return []
    } else if (!token.value) {
      return []
    } else if (additionEnable.value) {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/updatePageContent] categoryId is required but not provided')
        return []
      }
      const param = {
        categoryId: room.value.categoryId,
        timestamp: content.value.timestamp,
        end: index.value,
        appSecret,
      }
      const [categoryRes, additionRes] = await Promise.all([
        $request.send(gameApi.getListCategoryByRange(param)),
        $request.send(gameApi.getListAdditionByRange(param)),
      ])
      return [categoryRes, additionRes]
    } else {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/updatePageContent] categoryId is required but not provided')
        return []
      }
      const param = {
        categoryId: room.value.categoryId,
        timestamp: content.value.timestamp,
        end: index.value,
        appSecret,
      }
      const data = await $request.send(gameApi.getListCategoryByRange(param))
      return [data]
    }
  }

  const load = async () => {
    // 防止重複載入
    if (isLoadingMore.value) {
      return
    }

    setIsLoadingMore(true)

    try {
      await routing()
      setIsLoadingMore(false)
    } catch (error) {
      setIsLoadingMore(false)
      console.error('[useGame/load] ERROR:', error)
      throw error
    }
  }

  const nextPage = async () => {
    // 防止重複觸發
    if (isLoadingMore.value || isLastPage.value) {
      return
    }

    setIsLoadingMore(true)

    try {
      await routing()
    } catch (error) {
      setIsLoadingMore(false)
      console.error('[useGame/nextPage] ERROR:', error)
      throw error
    }
  }

  const routing = async () => {
    if (hasNext.value) {
      await loadPage()
    } else {
      setIsLoadingMore(false)
    }
  }

  const loadPage = async () => {
    store.setGameStatus('loading')
    increaseIndex()

    try {
      const data = await getPageContent()
      appendContent(data)

      setLastPage()

      store.setGameStatus('loaded')

      await nextTick()

      setIsLoadingMore(false)

      store.setGameStatus('idle')
    } catch (err) {
      setIsLoadingMore(false)
      store.setGameStatus('idle')
      console.error('[useGame/loadPage] ERROR:', err)
    }
  }

  const update = async () => {
    if (isLoadingMore.value) return

    try {
      const data = await updatePageContent()
      swapContent(data)
    } catch (err) {
      console.error('[useGame] update error:', err)
    } finally {
      store.setGameStatus('idle')
    }
  }

  const RETURN_PROVIDERS = new Set(['RSG', 'KP', 'EPICWIN', 'AMBSLOT', 'SEXY_VENUS', 'SEXY_SEXYBCRT', 'PG', 'NPG'])

  const buildPlayParams = ({ providerId, gameId, gameTypeId, mappingId, walletType: wt, entrySource, lang: langValue, isMobileValue }) => ({
    provider_id: providerId,
    game_id: gameId,
    gametype_id: gameTypeId,
    mapping_id: mappingId,
    is_demo: false,
    is_mobile: isMobileValue === 'mobile' ? 'y' : null,
    lang: langValue,
    entry_source: entrySource,
    selected_wallet_type: wt,
    return_url: RETURN_PROVIDERS.has(providerId) ? `${window.location.origin}${base.value}return` : window.location.href?.split('?')?.[0],
  })

  const assertConnectParams = (p, caller) => {
    const missing = []
    if (!p.providerId) missing.push('providerId')
    if (selectWalletEnable.value && !p.walletType) missing.push('walletType')
    if (!p.gameId) missing.push('gameId')
    if (!missing.length) return

    const err = new Error(`[useGame/${caller}] Missing required params: ${missing.join(', ')}`)
    if (nuxtApp.$sentry) {
      nuxtApp.$sentry.captureException(err, {
        level: 'error',
        tags: { caller, at: 'game/useGame.js', missing: missing.join(',') },
        extra: {
          providerId: p.providerId ?? null,
          walletType: p.walletType ?? null,
          gameId: p.gameId ?? null,
          gameTypeId: p.gameTypeId ?? null,
          mappingId: p.mappingId ?? null,
        },
      })
    }
    throw err
  }

  /**
   * 進入遊戲 API
   * @param {Object} [input] 顯式參數；未傳則讀 store 快照（供 refreshGame 使用）
   * @param {string} input.providerId
   * @param {string} input.gameId
   * @param {string} input.gameTypeId
   * @param {string} input.mappingId
   * @param {'CASH'|'ACTIVITY'} input.walletType
   * @param {string} input.lang
   * @param {string} input.entry_source
   * @param {string} input.isMobileValue
   */
  const connectGame = async (input = null) => {
    const snapshot = input ?? {
      providerId: game.value.providerId,
      gameId: game.value.gameId,
      gameTypeId: game.value.gameTypeId,
      mappingId: game.value.mappingId,
      walletType: walletType.value,
      lang: lang.value,
      entrySource: entry.value ?? 'Other',
      isMobileValue: isMobile.value,
    }

    assertConnectParams(snapshot, input ? 'connectGame' : 'connectGame:snapshot')

    const params = buildPlayParams(snapshot)
    const data = await $request.send(gameApi.playGame(params))
    const { type, redirect_url, gameToken: token, gameHtml: html, wallet_type } = data
    return { type, gameToken: token, gameHtml: html, gameURL: redirect_url, gameWalletType: wallet_type }
  }

  const demoGameConnect = async () => {
    const { providerId, gameId, gameTypeId, mappingId } = game.value

    const params = {
      site: clientId,
      provider_id: providerId,
      game_id: gameId,
      gametype_id: gameTypeId,
      mapping_id: mappingId,
      is_mobile: isMobile.value === 'mobile' ? 'y' : null,
      lang: lang.value,
      entry_source: entry.value ?? 'Other', // 儲存遊戲入口來源
      appSecret,
    }

    const data = await $request.send(gameApi.demoGame(params))
    const { type, redirect_url, gameToken: token, gameHtml: html, is_demo } = data
    const updatedGame = { ...game.value, isDemo: is_demo }
    store.setGame(updatedGame)
    return { type, gameToken: token, gameHtml: html, gameURL: redirect_url, game: updatedGame }
  }

  const disconnectGame = async (uidSite, providerId = game.value.providerId) => {
    // [防呆] 若缺失 providerId 則不進行離開遊戲請求
    if (!providerId) {
      return
    }
    const data = await $request.send(
      gameApi.logoutGame({
        uid_site: uidSite,
        provider_id: providerId,
      })
    )
    return data
  }

  /**
   * 進入遊戲的統一入口
   * @param {Object} gameData
   * @param {Object} gameData.game 遊戲節點（必含 providerId、gameId 等）
   * @param {string} gameData.locale BCP-47（如 'en-US'）
   */
  const playGame = async (gameData) => {
    // playGame 為 fire-and-forget 入口（19 個 callsite 皆未 .catch）；
    // 於函式邊界統一吞掉 enterGame 的 rejection（內部已 handleGameError 重置 state），
    // 避免任一分支冒泡為 unhandledrejection (Sentry CLIENT-PWA-SSR-16N)
    try {
      const checkWalletRule = checkActivityWalletRule.value // 是否需要判斷當前活動規則
      if (!selectWalletEnable.value) {
        // 不需選擇錢包，直接進入遊戲
        const onSuccessCallback = gameRouter.createGameSuccessCallback()
        await enterGame(gameData, onSuccessCallback)
        return
      }

      if (checkWalletRule) {
        // DEFAULT → 跳過選錢包，直接現金錢包進遊戲
        if (withdrawRule.value === 'DEFAULT') {
          if (checkBalance.value && Number(getUserInfo.value?.balance || 0) === 0) {
            nuxtApp.$toast.error(nuxtApp.$i18n.t('description.insufficient_balance'))
            return
          }
          const onSuccessCallback = gameRouter.createGameSuccessCallback()
          await enterGame({ ...gameData, walletType: 'CASH' }, onSuccessCallback)
          return
        }

        // FREE_LIMIT + 非限制遊戲（fromNotBonusGame）→ 跳過選錢包，直接現金錢包進遊戲
        if (withdrawRule.value?.includes('FREE_LIMIT') && gameData.game?.fromNotBonusGame) {
          if (checkBalance.value && Number(getUserInfo.value?.balance || 0) === 0) {
            nuxtApp.$toast.error(nuxtApp.$i18n.t('description.insufficient_balance'))
            return
          }
          const onSuccessCallback = gameRouter.createGameSuccessCallback()
          await enterGame({ ...gameData, walletType: 'CASH' }, onSuccessCallback)
          return
        }

        // 其他情況（FREE_DEFAULT 或 FREE_LIMIT 限制內遊戲）→ 顯示雙錢包選擇
        mainStore.setModal('ModalSelectWallet')
        mainStore.setModalParams({ doubleWallet: true, gameData })
      } else {
        mainStore.setModal('ModalSelectWallet')
        mainStore.setModalParams({ doubleWallet: true, gameData })
      }
    } catch {
      // enterGame 已於 catch 區塊 handleGameError 重置 state，rejection 在此收斂不外拋
    }
  }

  /**
   * 實際執行進入遊戲流程：清狀態 → 寫入新資料 → 組參數呼叫 API
   * 契約：gameData = { game: { providerId, gameId, ... }, locale, walletType }
   */
  const enterGame = async (gameData, onSuccessCallback = null) => {
    try {
      clearGameInfo()
      transitionGameStatus(null, 'connecting')
      setGameInfo(gameData)
      setLang(gameData.locale)
      setEntry(gameData.entry)
      setWalletType(gameData.walletType)

      // 以顯式參數呼叫，避免 async race 時讀到被清空的 store
      const { providerId, gameId, gameTypeId, mappingId } = gameData.game
      const result = await connectGame({
        providerId,
        gameId,
        gameTypeId,
        mappingId,
        walletType: gameData.walletType,
        lang: (gameData.locale || '').replace('-', '_'),
        entrySource: gameData.entry,
        isMobileValue: isMobile.value,
      })
      setGameURL(result)
      transitionGameStatus('connecting', 'playing')

      // 執行成功回調（如路由跳轉）
      if (onSuccessCallback && typeof onSuccessCallback === 'function') {
        await onSuccessCallback(gameData)
      }
    } catch (err) {
      handleGameError(err, 'enterGame')
      throw err // 重新拋出錯誤供上層處理
    }
  }

  const enterDemoGame = async (gameData, onSuccessCallback = null) => {
    try {
      clearGameInfo()
      transitionGameStatus(null, 'demoGame')
      setGameInfo(gameData)
      setLang(gameData.locale)
      setEntry(gameData.entry)

      const result = await demoGameConnect()
      setGameURL(result)
      transitionGameStatus('demoGame', 'playingDemo')

      // 執行成功回調
      if (onSuccessCallback && typeof onSuccessCallback === 'function') {
        await onSuccessCallback(gameData)
      }
    } catch (err) {
      handleGameError(err, 'demoGame')
      throw err
    }
  }

  const exitGame = async () => {
    // 取得當前遊戲資訊狀態 避免異步過程中 store 狀態變動
    const currentGameSnapshot = { ...game.value }
    const providerId = currentGameSnapshot?.providerId
    const currentUidSite = uidSite.value

    try {
      transitionGameStatus(null, 'disconnect')

      // [防呆] 若缺失 providerId 則視為已登出或無效狀態，直接返回，不報 Sentry
      if (!providerId) {
        console.warn('[Game] Missing providerId on exit. Possibly double-clicked leave game.')
        return
      }

      await disconnectGame(currentUidSite, providerId)

      setGameURL({ gameURL: null })
    } catch (err) {
      handleGameError(err, 'exitGame')
      throw err
    } finally {
      resetGameState(true)
    }
  }

  const exitDemoGame = () => {
    clearGameInfo()
    store.setGameStatus('idle')
  }

  const refreshGame = async (onSuccessCallback = null) => {
    // 前置檢查：拒絕在無效 store 狀態下刷新，避免污染 Kibana
    const providerId = game.value?.providerId
    const wt = walletType.value
    if (!providerId || !wt) {
      if (nuxtApp.$sentry) {
        nuxtApp.$sentry.captureMessage('REFRESH_GAME_INVALID_STATE', {
          level: 'warning',
          tags: { caller: 'refreshGame', at: 'game/useGame.js' },
          extra: { providerId: providerId ?? null, walletType: wt ?? null },
        })
      }
      // 不打 API。先清狀態再導航，避免 luckysport 頁面在 unmount 過程中的 watcher 觸到 in-flight 的遊戲實例
      resetGameState(true)
      try {
        await gameRouter.handleGameExit?.()
      } catch (navErr) {
        console.error('[useGame] refreshGame safe exit error:', navErr)
      }
      return
    }

    store.setGameStatus('refreshing')

    try {
      await disconnectGame(uidSite.value)
      setGameURL({ gameURL: null })

      store.setGameStatus('refreshPending')
      await new Promise((resolve) => setTimeout(resolve, 500))

      const result = await connectGame()
      setGameURL(result)
      store.setGameStatus('connecting')

      // 執行成功回調
      if (onSuccessCallback && typeof onSuccessCallback === 'function') {
        await onSuccessCallback(result)
      }
    } catch (err) {
      // catch 內已完整處理（清 gameURL + 重置 idle + log）；兩個 watch caller 皆未 .catch，
      // 不再外拋，避免冒泡為 unhandledrejection (Sentry CLIENT-PWA-SSR-16N)
      setGameURL({ gameURL: null })
      store.setGameStatus('idle')
      console.error('[useGame] refreshGame error:', err)
    }
  }

  const startAutoUpdate = async () => {
    if (!import.meta.client) return

    stopAutoUpdate()

    // 立即執行一次更新
    if (hasRoomAndIsNotInGame.value) {
      await update()
    }

    // 開始定時更新
    ctx.updateTimer = setInterval(async () => {
      if (hasRoomAndIsNotInGame.value) {
        await update()
      }
    }, 20000)
  }

  const stopAutoUpdate = () => {
    if (!import.meta.client) return

    if (ctx.updateTimer) {
      clearInterval(ctx.updateTimer)
      ctx.updateTimer = null
    }
  }

  // 元件卸載時清理 timer，避免記憶體洩漏
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      stopAutoUpdate()
    })
  }

  return {
    base,
    isLastPage,
    isLoadingMore,
    room,
    index,
    content,
    additionEnable,
    addition,
    game,
    walletType,
    lang,
    entry,
    isMobile,
    gameURL,
    gameToken,
    gameHtml,
    gameType,
    gameWalletType,
    isPlaying,
    gameStatus,
    hasNext,
    hasRoomAndIsNotInGame,
    // 工具函數
    setRoom,
    recoverSetRoom,
    load,
    nextPage,
    // 遊戲控制函數（已優化）
    playGame,
    enterGame,
    enterDemoGame,
    connectGame,
    exitGame,
    exitDemoGame,
    refreshGame,
    // 狀態管理函數
    resetContent,
    resetGameState,
    transitionGameStatus,
    handleGameError,
    setIsPlaying,
    setIsLoadingMore,
    setWalletType,
    setLang,
    setEntry,
    // 自動更新
    startAutoUpdate,
    stopAutoUpdate,
  }
}
